import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDown, Download, Play } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';
import { MEDIA } from '@/data/plotData';
import { LANDING } from '@/constants/testIds';

const heroImages = [MEDIA.heroWide, MEDIA.droneAerial, MEDIA.droneOblique];

const lineReveal = {
  hidden: { y: '110%', opacity: 0 },
  visible: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.4 + i * 0.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { y: 30, opacity: 0 },
  visible: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.8 + i * 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function HeroSection({ onOpenBrochure }) {
  const { lang } = useLanguage();
  const t = translations[lang].hero;
  const [currentImg, setCurrentImg] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  const nextImage = useCallback(() => {
    setCurrentImg((prev) => (prev + 1) % heroImages.length);
  }, []);

  useEffect(() => {
    const interval = setInterval(nextImage, 5000);
    return () => clearInterval(interval);
  }, [nextImage]);

  const scrollToPlots = () => {
    document.getElementById('plots')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid={LANDING.hero}
      className="relative min-h-[100vh] flex items-end overflow-hidden"
    >
      {/* Background Image Carousel */}
      <AnimatePresence mode="sync">
        <motion.div
          key={currentImg}
          initial={{ scale: 1.08, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <img
            src={heroImages[currentImg]}
            alt="Lakshmi Nivasa aerial view"
            className="w-full h-full object-cover"
            loading="eager"
          />
        </motion.div>
      </AnimatePresence>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10 z-[1]" />

      {/* Grain texture overlay */}
      <div className="absolute inset-0 opacity-[0.03] z-[1]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      {/* Image indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2 sm:hidden">
        {heroImages.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentImg(i)}
            className={`h-1 rounded-full transition-all duration-300 ${
              i === currentImg ? 'w-6 bg-[#D48C57]' : 'w-2 bg-white/40'
            }`}
            aria-label={`Image ${i + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 pt-32">
        <div className="max-w-3xl">
          {/* Pre-title tag */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="w-8 h-px bg-[#D48C57]" />
            <span className="text-[#D48C57] text-xs sm:text-sm font-medium tracking-[0.15em] uppercase">
              {t.pre}
            </span>
          </motion.div>

          {/* Main Title - Line by line reveal */}
          <h1
            data-testid={LANDING.heroTitle}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white tracking-tight leading-[1.05]"
          >
            <span className="block overflow-hidden">
              <motion.span
                className="block"
                variants={lineReveal}
                initial="hidden"
                animate="visible"
                custom={0}
              >
                {t.title}
              </motion.span>
            </span>
            <span className="block overflow-hidden mt-1">
              <motion.span
                className="block text-[#D48C57]"
                variants={lineReveal}
                initial="hidden"
                animate="visible"
                custom={1}
              >
                {t.titleAccent}
              </motion.span>
            </span>
          </h1>

          {/* Subtitle */}
          <motion.p
            data-testid={LANDING.heroSubtitle}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
            className="mt-6 sm:mt-8 text-white/80 text-base sm:text-lg max-w-xl leading-relaxed"
          >
            {t.subtitle}
          </motion.p>

          {/* CTAs */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
            className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <button
              data-testid={LANDING.heroCta}
              onClick={scrollToPlots}
              className="group flex items-center justify-center gap-2 bg-[#D48C57] hover:bg-[#C07A4A] text-white px-7 py-3.5 rounded-full text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-[#D48C57]/20"
            >
              {t.cta}
              <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
            </button>
            <button
              onClick={onOpenBrochure}
              data-testid={LANDING.ctaBrochure}
              className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm px-7 py-3.5 rounded-full text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 border border-white/20"
            >
              <Download className="w-4 h-4" />
              {t.ctaSecondary}
            </button>
            <button
              onClick={() => setShowVideo(true)}
              data-testid="hero-video-cta"
              className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm px-7 py-3.5 rounded-full text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 border border-white/20"
            >
              <Play className="w-4 h-4" />
              {lang === 'en' ? 'Watch Flythrough' : 'ஃப்ளைத்ரூ பாருங்கள்'}
            </button>
          </motion.div>

          {/* Fullscreen YouTube Player Overlay */}
          <AnimatePresence>
            {showVideo && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
                onClick={() => setShowVideo(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="relative w-full max-w-4xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setShowVideo(false)}
                    data-testid="video-close-btn"
                    className="absolute -top-10 right-0 w-8 h-8 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition-colors"
                    aria-label="Close video"
                  >
                    <span className="text-white text-lg leading-none">&times;</span>
                  </button>
                  <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                    <iframe
                      src="https://www.youtube.com/embed/SwCJT8Z6nBM?autoplay=1&rel=0"
                      title="Lakshmi Nivasa Flythrough"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                      style={{ border: 0 }}
                    />
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Scroll indicator + image dots (desktop) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="hidden sm:flex absolute bottom-8 right-8 flex-col items-center gap-4"
        >
          {/* Image dots */}
          <div className="flex flex-col gap-1.5">
            {heroImages.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentImg(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === currentImg ? 'w-1.5 h-6 bg-[#D48C57]' : 'w-1.5 h-1.5 bg-white/40'
                }`}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent"
          />
        </motion.div>
      </div>
    </section>
  );
}
